<template>
  <!-- Mode CARTE -->
  <div v-if="viewMode === 'card'"
    class="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 cursor-pointer border border-gray-200 relative"
    :class="{ 'ring-2 ring-blue-500 ring-opacity-50': selected }"
    @click="handleCardClick">
    
    <!-- Badge de propriété - EN HAUT À DROITE -->
    <div class="absolute top-2 right-2 z-5">
      <!-- Affichage pour les collections appartenant au user : icône œil privé/public -->
      <div v-if="isOwned" class="relative group">
        <div v-if="!collection.is_public"
          class="w-6 h-6 rounded-full flex items-center justify-center bg-red-100 text-red-600">
          <EyeOffIcon class="w-4 h-4" />
        </div>
        <div v-else class="w-6 h-6 rounded-full flex items-center justify-center bg-green-100 text-green-600">
          <EyeIcon class="w-4 h-4" />
        </div>
        <!-- Tooltip -->
        <div class="anamnesis-tooltip">
          <div class="anamnesis-tooltip-content">
            {{ collection.is_public ? 'Visible par tous' : 'Visible par vous seulement' }}
          </div>
          <div class="anamnesis-tooltip-arrow"></div>
        </div>
      </div>
      <!-- Affichage pour les collections publiques d'autres users -->
      <div v-else-if="collection.is_public" class="relative group">
        <div class="w-6 h-6 rounded-full flex items-center justify-center bg-green-100 text-green-600">
          <GlobeIcon class="w-4 h-4" />
        </div>
        <!-- Tooltip -->
        <div class="anamnesis-tooltip">
          <div class="anamnesis-tooltip-content">
            Collection publique
          </div>
          <div class="anamnesis-tooltip-arrow"></div>
        </div>
      </div>
    </div>
    
    <div class="p-4">
      <!-- Header avec badges -->
      <div class="flex items-start justify-between mb-3">
        <div class="flex-1 min-w-0">
          <h3 class="font-semibold text-lg text-gray-800 truncate" :title="collection.name">
            {{ collection.name }}
          </h3>
          <p v-if="collection.description" class="text-gray-500 text-sm mt-1 line-clamp-2">
            {{ collection.description }}
          </p>
        </div>
      </div>

      <!-- Informations -->
      <div class="flex items-center justify-between text-sm text-gray-500">
        <div class="flex items-center space-x-4">
          <span class="flex items-center">
            <FolderIcon class="h-4 w-4 mr-1" />
            {{ collection.memos_count || 0 }} mémo{{ (collection.memos_count || 0) > 1 ? 's' : '' }}
          </span>
          <span class="flex items-center">
            <CalendarIcon class="h-4 w-4 mr-1" />
            {{ formatDate(collection.created_at) }}
          </span>
        </div>
        
        <!-- Actions -->
        <div class="flex items-center space-x-1" @click.stop>
          <button
            v-if="showEditButton && isOwned"
            class="p-1 text-gray-400 hover:text-blue-600 transition-colors"
            title="Éditer la collection"
            @click="handleEdit"
          >
            <PencilIcon class="h-4 w-4" />
          </button>
          <button
            v-if="showDeleteButton && isOwned"
            class="p-1 text-gray-400 hover:text-red-600 transition-colors"
            title="Supprimer la collection"
            @click="handleDelete"
          >
            <TrashIcon class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Mode TABLE -->
  <div v-else-if="viewMode === 'table'"
    class="grid grid-cols-[auto_2fr_1fr_1fr_auto_auto] cursor-pointer gap-4 sm:gap-2 p-3 sm:p-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 border border-gray-200"
    :class="{ 'ring-2 ring-blue-500 ring-opacity-50': selected }"
    @click="handleCardClick">
    
    <!-- Checkbox de sélection -->
    <div v-if="selectable" class="flex items-center justify-center" @click.stop>
      <input 
        type="checkbox"
        :checked="selected"
        class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        @change="emitToggleSelection"
      />
    </div>

    <!-- Nom et description -->
    <div class="min-w-0">
      <div class="flex items-center gap-2">
        <FolderIcon class="h-4 w-4 text-gray-600 shrink-0" />
        <span class="font-medium text-gray-900 truncate" :title="collection.name">
          {{ collection.name }}
        </span>
      </div>
      <p v-if="collection.description" class="text-sm text-gray-500 truncate mt-1">
        {{ collection.description }}
      </p>
    </div>

    <!-- Nombre de mémos -->
    <div class="flex items-center">
      <span class="text-sm text-gray-600">
        {{ collection.memos_count || 0 }} mémo{{ (collection.memos_count || 0) > 1 ? 's' : '' }}
      </span>
    </div>

    <!-- Date de création -->
    <div class="flex items-center">
      <span class="text-sm text-gray-500">
        {{ formatDate(collection.created_at) }}
      </span>
    </div>

    <!-- Badge visibilité -->
    <div class="flex items-center">
      <!-- Affichage pour les collections appartenant au user -->
      <div v-if="isOwned" class="relative group">
        <div v-if="!collection.is_public"
          class="w-4 h-4 rounded-full flex items-center justify-center bg-red-100 text-red-600">
          <EyeOffIcon class="w-3 h-3" />
        </div>
        <div v-else class="w-4 h-4 rounded-full flex items-center justify-center bg-green-100 text-green-600">
          <EyeIcon class="w-3 h-3" />
        </div>
      </div>
      <!-- Affichage pour les collections publiques d'autres users -->
      <div v-else-if="collection.is_public" class="relative group">
        <div class="w-4 h-4 rounded-full flex items-center justify-center bg-green-100 text-green-600">
          <GlobeIcon class="w-3 h-3" />
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center space-x-1" @click.stop>
      <button
        v-if="showEditButton && isOwned"
        class="p-1 text-gray-400 hover:text-blue-600 transition-colors"
        title="Éditer la collection"
        @click="handleEdit"
      >
        <PencilIcon class="h-4 w-4" />
      </button>
      <button
        v-if="showDeleteButton && isOwned"
        class="p-1 text-gray-400 hover:text-red-600 transition-colors"
        title="Supprimer la collection"
        @click="handleDelete"
      >
        <TrashIcon class="h-4 w-4" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MemoCollection } from '@/api/types/memos/collections';
import { useAuthStore } from '@/stores/auth';
import {
    Calendar as CalendarIcon,
    Folder as FolderIcon,
    Pencil as PencilIcon,
    Trash as TrashIcon,
    Eye as EyeIcon,
    EyeOff as EyeOffIcon,
    Globe as GlobeIcon
} from 'lucide-vue-next';
import { computed } from 'vue';

interface Props {
  collection: MemoCollection;
  viewMode?: 'card' | 'table';
  selectable?: boolean;
  selected?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  viewMode: 'card',
  selectable: false,
  selected: false,
  showEditButton: true,
  showDeleteButton: true,
});

const authStore = useAuthStore();
const currentUserId = computed(() => authStore.user?.uuid || '');
const isOwned = computed(() => props.collection.user_id === currentUserId.value);

const emit = defineEmits<{
  click: [collection: MemoCollection];
  edit: [collection: MemoCollection];
  delete: [collection: MemoCollection];
  'toggle-selection': [uuid: string];
}>();

const handleCardClick = (): void => {
  emit('click', props.collection);
};

const handleEdit = (): void => {
  emit('edit', props.collection);
};

const handleDelete = (): void => {
  emit('delete', props.collection);
};

const emitToggleSelection = (): void => {
  emit('toggle-selection', props.collection.uuid);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('fr-FR');
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.anamnesis-tooltip {
  visibility: hidden;
  position: absolute;
  z-index: 9999;
  top: -3rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.25rem 0.75rem;
  pointer-events: none;
}

.anamnesis-tooltip-content {
  background-color: rgb(17, 24, 39);
  color: white;
  font-size: 0.75rem;
  border-radius: 0.375rem;
  padding: 0.5rem;
  white-space: nowrap;
}

.anamnesis-tooltip-arrow {
  position: absolute;
  left: 50%;
  bottom: -0.25rem;
  transform: translateX(-50%) rotate(45deg);
  width: 1rem;
  height: 1rem;
  background-color: rgb(17, 24, 39);
}

.group:hover .anamnesis-tooltip {
  visibility: visible;
}
</style> 